import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, TrendingDown, LineChart as LineChartIcon } from "lucide-react";
import { useApi, useFetch, lastMonth, errMsg } from "../lib/api";
import { useMember } from "../lib/member";
import { Pill, Empty } from "./shared";
import { Button } from "./ui/button";
import { ScoreTrendDialog } from "./ScoreTrend";
import { ScoreCompareDialog } from "./ScoreCompare";
import { MonthPicker } from "./DateField";

const outline = "border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white";

const GRADE = { A: "green", B: "blue", C: "amber", D: "red" };
const BAR = { A: "bg-emerald-400", B: "bg-cyan-400", C: "bg-amber-400", D: "bg-rose-400" };
const show = (v) => (v == null || v === "" ? "—" : String(v));

const Trend = ({ trend }) => (
  <div className="flex items-end gap-1 h-10" data-testid="pe-score-trend">
    {trend.map((t) => (
      <div key={t.month} title={`${t.month}: ${t.score ?? "no wages"}`} className="flex flex-col items-center gap-0.5">
        <div className={`w-3 rounded-sm ${t.score == null ? "bg-slate-700" : BAR[t.grade]}`} style={{ height: `${Math.max(3, (t.score || 0) * 0.32)}px` }} />
        <span className="text-[9px] text-slate-500 font-mono">{t.month.slice(5)}</span>
      </div>))}
  </div>);

const ScoreCard = ({ c, onOpen }) => (
  <div className="c-surface p-5 c-rise grid gap-4 md:grid-cols-[220px_1fr_auto] items-start" data-testid="pe-score-card">
    <div>
      <button className={`font-semibold text-left transition-colors ${onOpen ? "hover:text-[#00E599]" : "cursor-default"}`} onClick={onOpen} data-testid="pe-score-vendor">{c.vendor_name}</button>
      <div className="text-xs text-slate-500">{c.workers} worker(s) paid · 6-month avg {show(c.average_6m)}</div>
      <div className="mt-3 flex items-baseline gap-3">
        <span className="font-display text-4xl font-semibold" data-testid="pe-score-value">{show(c.score)}</span>
        {c.grade && <Pill tone={GRADE[c.grade]} testid="pe-score-grade">Grade {c.grade}</Pill>}
      </div>
      {c.in_progress && <div className="mt-2"><Pill tone="slate" testid="pe-score-in-progress">In progress · not due yet</Pill></div>}
      {c.raises_unpaid > 0 && <div className="mt-2" title="Workers not yet confirmed as paid a new minimum wage, 7+ days after it started (current status, not part of the score)"><Pill tone="red" testid="pe-score-raises-unpaid">{c.raises_unpaid} raise(s) unpaid</Pill></div>}
    </div>
    <div className="grid gap-2">{c.breakdown.length ? c.breakdown.map((b) => (
      <div key={b.key} className="grid grid-cols-[170px_1fr_52px] items-center gap-3 text-xs" data-testid={`pe-score-${b.key}`}>
        <span className="text-slate-300">{b.label}</span>
        <div><div className="h-1.5 rounded bg-[#1A2234]"><div className={`h-1.5 rounded ${b.points === b.max ? "bg-emerald-400" : b.points ? "bg-amber-400" : "bg-rose-400"}`} style={{ width: `${(b.points / b.max) * 100}%` }} /></div>
          <div className="text-[11px] text-slate-500 mt-0.5">{b.note}</div></div>
        <span className="font-mono text-right">{b.points}/{b.max}</span>
      </div>)) : <p className="text-xs text-slate-500">No wages entered for this month, so there's nothing to score.</p>}
      {(c.penalties || []).map((p) => (
        <div key={p.key} className="grid grid-cols-[170px_1fr_52px] items-center gap-3 text-xs" data-testid={`pe-score-penalty-${p.key}`}>
          <span className="text-rose-300">{p.label}</span><div className="text-[11px] text-slate-500">{p.note}</div>
          <span className="font-mono text-right text-rose-300">−{p.points}</span></div>))}</div>
    {onOpen ? <button onClick={onOpen} className="grid justify-items-center gap-1 rounded-md p-1 hover:bg-[#1A2234] transition-colors" title="Open 12-month trend" data-testid="pe-score-trend-open">
      <Trend trend={c.trend} /><span className="text-[10px] text-slate-400">12-month trend →</span></button>
      : <div className="grid justify-items-center gap-1 p-1"><Trend trend={c.trend} /></div>}
  </div>);

const ScoreActions = ({ eid, month }) => {
  const api = useApi();
  const { me } = useMember();
  const [busy, setBusy] = useState(false);
  const exportXlsx = async () => {
    try {
      const r = await api.get(`/pe/${eid}/scorecard.xlsx`, { params: { month }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `Scorecard_${month}.xlsx`;
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("Download failed"); }
  };
  const email = async () => {
    if (!window.confirm("Email last month's scorecard now to admins, the compliance inbox and principal employer approvers?")) return;
    setBusy(true);
    try { const r = (await api.post("/pe/scorecards/email")).data; toast.success(`Scorecard for ${r.month} emailed (${r.sent} email(s) sent)`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" className={outline} onClick={exportXlsx} data-testid="pe-score-export"><Download size={14} className="mr-1.5" />Export Excel</Button>
      {me?.role === "admin" && <Button variant="outline" className={outline} onClick={email} disabled={busy} data-testid="pe-score-email"><Mail size={14} className="mr-1.5" />Email last month now</Button>}
    </div>);
};

export const ContractorScorecard = ({ eid }) => {
  const [month, setMonth] = useState(lastMonth());
  const { data } = useFetch(`/pe/${eid}/scorecard?month=${month}`);
  const [open, setOpen] = useState(null);
  const [compare, setCompare] = useState(false);
  const { me } = useMember();
  return (
    <div className="grid gap-4" data-testid="pe-scorecard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-2xl">Each contractor is scored out of 100 every month. PF filed on time is worth 30 points, ESIC filed on time 25, PF challan matching the ECR 20, ESIC challan matching the ESIC file 10, and no payment hold 15. Late filing earns half the points (rounded up). Penalties are then taken off: up to 20 points when workers are paid below minimum wage (scaled by the share of workers, at least 5), and 15 while the CLRA licence is expired. Grades: A 85+, B 70+, C 50+, D below 50. Months not yet due are shown as in progress and left out of the 6-month average. Last month's scorecard is emailed on the 20th of every month.</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid gap-1"><span className="text-xs text-slate-400">Month</span>
            <MonthPicker value={month} onChange={setMonth} testid="pe-score-month" className="w-44" /></label>
          <ScoreActions eid={eid} month={month} />
          {["admin", "employer"].includes(me?.role) && <button onClick={() => setCompare(true)} className="inline-flex items-center gap-1.5 rounded-md border border-[#00E599]/40 px-3 py-2 text-sm text-[#00E599] hover:bg-[#00E599]/10" data-testid="pe-score-compare-open">
            <LineChartIcon size={14} />Compare all</button>}</div>
      </div>
      {compare && <ScoreCompareDialog eid={eid} month={month} onClose={() => setCompare(false)} />}
      {!data && <p className="text-xs text-slate-500 font-mono" data-testid="pe-score-loading">Loading scorecard…</p>}
      {data && !data.contractors.length && <Empty text="No contractors are linked to this principal employer yet." />}
      {(data?.contractors || []).map((c) => <ScoreCard key={c.vendor_id} c={c} onOpen={["admin", "employer"].includes(me?.role) ? () => setOpen(c) : undefined} />)}
      {open && <ScoreTrendDialog eid={eid} vendor={open} month={month} onClose={() => setOpen(null)} />}
    </div>);
};

export const RuleHistory = ({ eid }) => {
  const { data } = useFetch(`/pe/${eid}/rule-changes`);
  if (!data) return <p className="text-xs text-slate-500 font-mono">Loading…</p>;
  if (!data.length) return <Empty text="No state rule changes recorded yet." testid="pe-rule-history-empty" />;
  return (
    <div className="c-surface overflow-x-auto c-rise" data-testid="pe-rule-history">
      <table className="c-table w-full">
        <thead><tr><th>When</th><th>Changed by</th><th>Where</th><th>State</th><th>Changes</th></tr></thead>
        <tbody>{data.map((r) => (
          <tr key={r.id} data-testid="pe-rule-history-row">
            <td className="font-mono text-xs whitespace-nowrap">{new Date(r.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
            <td className="text-sm" data-testid="pe-rule-history-by">{r.by}<div className="text-[11px] text-slate-500">{r.by_role}</div></td>
            <td className="text-xs">{r.source}</td><td className="text-xs">{show(r.state)}</td>
            <td className="text-xs grid gap-0.5">{r.changes.map((c) => (
              <div key={c.field}><span className="text-slate-400">{c.label}:</span> <span className="line-through text-slate-500">{show(c.old)}</span> → <span className="text-[#00E599]">{show(c.new)}</span></div>))}</td>
          </tr>))}</tbody>
      </table>
    </div>);
};

export const LowestScores = () => {
  const { me } = useMember();
  const [basis, setBasis] = useState("month");
  const { data } = useFetch(`/pe/scorecards/lowest?basis=${basis}`);
  return (
    <div className="c-surface p-6 c-rise mt-6" data-testid="dashboard-lowest-scores">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><div className="c-eyebrow mb-1 flex items-center gap-1.5"><TrendingDown size={13} className="text-rose-400" />Lowest-scoring contractors</div>
          <div className="text-xs text-slate-500">{basis === "month" ? `Score for ${data?.month || "last month"}` : "Average of the last 6 months"} · {me?.role === "employer" ? "your contractors" : "all principal employers"}</div></div>
        <div className="flex rounded-md border border-[#1F2A3E] p-0.5 text-xs" data-testid="lowest-basis-toggle">
          {[["month", "Last month"], ["avg", "6-month average"]].map(([k, l]) => (
            <button key={k} onClick={() => setBasis(k)} data-testid={`lowest-basis-${k}`}
              className={`px-2.5 py-1 rounded transition-colors ${basis === k ? "bg-[#00E599] text-[#0B1017] font-semibold" : "text-slate-400 hover:text-white"}`}>{l}</button>))}
        </div>
      </div>
      {!data && <p className="text-xs text-slate-500 font-mono">Loading…</p>}
      {data && !data.contractors.length && <Empty text="No scored contractors yet." testid="lowest-empty" />}
      <ol className="grid gap-2">{(data?.contractors || []).map((c, i) => (
        <li key={c.employer_id + c.vendor_id} data-testid="lowest-score-row">
          <Link to={`/contractors/${c.vendor_id}`} className="grid grid-cols-[24px_1fr_auto_auto] items-center gap-3 rounded-lg border border-[#1F2A3E] bg-[#0B1017] px-3 py-2.5 hover:border-rose-500/40 transition-colors">
            <span className="font-mono text-xs text-slate-500">{i + 1}</span>
            <span className="min-w-0"><span className="text-sm block truncate" data-testid="lowest-vendor">{c.vendor_name}</span>
              <span className="text-[11px] text-slate-500 block truncate">{c.employer_name}{basis === "month" && c.in_progress ? " · in progress" : ""}</span></span>
            <span className="font-mono text-lg font-semibold" data-testid="lowest-value">{c.value}</span>
            <Pill tone={GRADE[c.grade]} testid="lowest-grade">{c.grade}</Pill>
          </Link></li>))}</ol>
    </div>);
};

export const MyScoreCard = () => {
  const { data } = useFetch("/pe/my-scorecard");
  if (!data) return null;
  return (
    <Link to="/my-score" className="c-surface p-6 c-rise mt-6 grid gap-4 md:grid-cols-[200px_1fr_auto] items-center hover:border-[#00E599]/40 transition-colors" data-testid="dashboard-my-score">
      <div><div className="c-eyebrow mb-1">My compliance score · {data.month}</div>
        <div className="flex items-baseline gap-3 mt-2"><span className="font-display text-4xl font-semibold" data-testid="my-score-value">{show(data.score)}</span>
          {data.grade && <Pill tone={GRADE[data.grade]} testid="my-score-grade">Grade {data.grade}</Pill>}</div>
        {data.in_progress && <div className="text-[11px] text-slate-500 mt-1">In progress · not due yet</div>}</div>
      <div className="grid gap-1 text-xs" data-testid="my-score-lost">
        {data.score == null ? <span className="text-slate-500">No wages entered for this month yet.</span>
          : data.lost.length ? data.lost.map((b) => <div key={b.key}><span className="text-rose-300">−{b.max - b.points}</span> <span className="text-slate-300">{b.label}</span> <span className="text-slate-500">· {b.note}</span>
              {b.tip && <div className="text-emerald-200/80 pl-4" data-testid={`my-score-card-tip-${b.key}`}>→ {b.tip}</div>}</div>)
            : <span className="text-emerald-300">Full marks — no points lost this month.</span>}
      </div>
      <span className="text-xs text-slate-400">Details & 12-month trend →</span>
    </Link>);
};
